import {
  pgTable,
  pgEnum,
  uuid,
  varchar,
  integer,
  text,
  jsonb,
  timestamp,
  boolean,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";
import { baFinancialPeriods } from "./schema-business-analytics.js";
import { organisations } from "./schema-security.js";

// ============================================================================
// DOCUMENT EXTRACTION MODULE — PDF Import (SPRINT-23)
// Table: crm_x_pdf_extraction_jobs
// Purpose: Track PDF extraction job lifecycle, provider references,
//          and extracted financial data mapped back to financial periods.
// ============================================================================

// ============================================================================
// ENUMS
// ============================================================================

export const pdfExtractionStatusEnum = pgEnum("pdf_extraction_status", [
  "pending",
  "uploading",
  "processing",
  "completed",
  "failed",
  "cancelled",
  "intent",
  "upload_leased",
  "uploaded",
  "queued",
  "redacted",
  "claim_leased",
  "submission_started",
  "provider_accepted",
  "submission_unknown",
  "quarantined",
  "expired",
  "purged",
]);

// ============================================================================
// TABLE: crm_x_pdf_extraction_jobs
// ============================================================================

export const pdfExtractionJobs = pgTable("crm_x_pdf_extraction_jobs", {
  id: uuid("id").primaryKey().defaultRandom(),
  organisationId: uuid("organisation_id")
    .notNull()
    .references(() => organisations.id, { onDelete: "restrict" }),
  initiatingActorId: text("initiating_actor_id").notNull(),
  ocrJobId: uuid("ocr_job_id").notNull().unique(),
  financialPeriodId: uuid("financial_period_id")
    .notNull()
    .references(() => baFinancialPeriods.id, { onDelete: "cascade" }),
  // Text preserves historical provider labels; new jobs use the insert allowlist.
  provider: text("provider").notNull(),
  status: pdfExtractionStatusEnum("status").notNull().default("pending"),
  fileName: varchar("file_name", { length: 500 }).notNull(),
  fileSize: integer("file_size"),
  /**
   * EDG-17648: storage key of the durable Restricted source-document record in
   * `crm_x_financial_source_documents.storage_path`. Written when the source
   * document is persisted (before the provider is called) and read by the
   * download path. The job row is NOT the record's referent — the record hangs
   * off the financial period and outlives this job and its working copy.
   */
  declaredContentType: varchar("declared_content_type", { length: 100 })
    .notNull()
    .default("application/pdf"),
  declaredByteLength: integer("declared_byte_length"),
  fileStoragePath: varchar("file_storage_path", { length: 1000 }),
  providerFileId: varchar("provider_file_id", { length: 255 }),
  providerRunId: varchar("provider_run_id", { length: 255 }),
  mappedData: jsonb("mapped_data"),
  confidenceScores: jsonb("confidence_scores"),
  errorMessage: text("error_message"),
  attempts: integer("attempts").notNull().default(0),
  maxAttempts: integer("max_attempts").notNull().default(3),
  deadLetteredAt: timestamp("dead_lettered_at", { withTimezone: true }),
  idempotencyKey: text("idempotency_key").unique(),
  ocrExpiresAt: timestamp("ocr_expires_at", { withTimezone: true }),
  uploadCompletedAt: timestamp("upload_completed_at", { withTimezone: true }),
  submissionStartedAt: timestamp("submission_started_at", { withTimezone: true }),
  submissionUnknownAt: timestamp("submission_unknown_at", { withTimezone: true }),
  providerAcknowledgedAt: timestamp("provider_acknowledged_at", { withTimezone: true }),
  cancelledAt: timestamp("cancelled_at", { withTimezone: true }),
  safeReasonCode: varchar("safe_reason_code", { length: 100 }),
  startedAt: timestamp("started_at", { withTimezone: true }),
  completedAt: timestamp("completed_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// ============================================================================
// TABLE: crm_x_financial_source_documents (EDG-17648)
//
// Durable, first-class home for the client-supplied financial source document
// (P&L / balance sheet) that a serviceability assessment relied on. Edgeview is
// a credit licensee: under DA-S11 the Restricted tier carries a 7-year
// obligation, and the source evidence is part of the record set, not merely an
// input to it.
//
// Placement (the CRM X design call EDG-17640 deliberately left open): the record
// hangs off the FINANCIAL PERIOD, not off the extraction job. The period is the
// domain entity the document actually evidences, it is the only referent
// resolvable at upload time, and it keeps the record addressable when the job is
// gone. Deal- and entity-document placement were rejected: neither is derivable
// from an extraction upload without inventing a linkage, and neither table has
// any storage, classification or retention machinery behind it today.
//
// Lifetime is independent of the extraction working copy by design — releasing
// the job-scoped working copy (the provider file and the pending upload) does
// not touch this record.
// ============================================================================

export const financialSourceDocuments = pgTable("crm_x_financial_source_documents", {
  id: uuid("id").primaryKey().defaultRandom(),
  financialPeriodId: uuid("financial_period_id")
    .notNull()
    .references(() => baFinancialPeriods.id, { onDelete: "restrict" }),

  // Original client-supplied identity — preserved verbatim for the record set.
  fileName: varchar("file_name", { length: 500 }).notNull(),
  fileSize: integer("file_size").notNull(),
  contentType: varchar("content_type", { length: 100 }).notNull().default("application/pdf"),
  /** SHA-256 (hex) of the stored bytes, captured at persist. Byte-identity proof. */
  contentHash: varchar("content_hash", { length: 64 }).notNull(),

  // Durable location. Unique so a storage key resolves to exactly one record.
  storagePath: varchar("storage_path", { length: 1000 }).notNull().unique(),
  storageProvider: varchar("storage_provider", { length: 20 }).notNull(),

  // DA-S11 Restricted posture. Enforced by CHECK constraints in the migration —
  // there is no lawful row of this table at any other classification.
  classification: varchar("classification", { length: 20 }).notNull().default("restricted"),
  sc06MemoryAllowed: boolean("sc06_memory_allowed").notNull().default(false),
  retentionRuleRef: varchar("retention_rule_ref", { length: 50 }).notNull().default("DA-S11"),
  /** createdAt + SOURCE_DOCUMENT_RETENTION_YEARS. Not a deletion trigger; a floor. */
  retainUntil: timestamp("retain_until", { withTimezone: true }).notNull(),

  /** Acting user id from auth (a Clerk id, not a UUID); #2226. */
  uploadedByUserId: text("uploaded_by_user_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
});

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

export const insertPdfExtractionJobSchema = createInsertSchema(pdfExtractionJobs, {
  financialPeriodId: z.string().uuid(),
  provider: z.enum(["claude_vision"]),
  status: z.enum([
    "pending",
    "uploading",
    "processing",
    "completed",
    "failed",
    "cancelled",
    "intent",
    "upload_leased",
    "uploaded",
    "queued",
    "redacted",
    "claim_leased",
    "submission_started",
    "provider_accepted",
    "submission_unknown",
    "quarantined",
    "expired",
    "purged",
  ]).optional(),
  fileName: z.string().min(1).max(500),
  fileSize: z.number().int().positive().optional(),
  providerFileId: z.string().max(255).optional(),
  providerRunId: z.string().max(255).optional(),
  mappedData: z.record(z.unknown()).optional(),
  confidenceScores: z.record(z.unknown()).optional(),
  errorMessage: z.string().optional(),
  attempts: z.number().int().nonnegative().optional(),
  maxAttempts: z.number().int().positive().optional(),
  deadLetteredAt: z.string().optional(),
  idempotencyKey: z.string().optional(),
  declaredContentType: z.literal("application/pdf").optional(),
  declaredByteLength: z.number().int().positive().max(20 * 1024 * 1024).optional(),
  startedAt: z.string().optional(),
  completedAt: z.string().optional(),
}).omit({
  id: true,
  organisationId: true,
  initiatingActorId: true,
  ocrJobId: true,
  ocrExpiresAt: true,
  uploadCompletedAt: true,
  submissionStartedAt: true,
  submissionUnknownAt: true,
  providerAcknowledgedAt: true,
  cancelledAt: true,
  safeReasonCode: true,
  fileStoragePath: true,
  createdAt: true,
  updatedAt: true,
});

export const updatePdfExtractionJobSchema = insertPdfExtractionJobSchema.partial();

export const selectPdfExtractionJobSchema = createSelectSchema(pdfExtractionJobs);

// ============================================================================
// TYPESCRIPT TYPES
// ============================================================================

export type PdfExtractionJob = typeof pdfExtractionJobs.$inferSelect;
export type InsertPdfExtractionJob = typeof pdfExtractionJobs.$inferInsert;

// ============================================================================
// FINANCIAL SOURCE DOCUMENTS — ZOD + TYPES (EDG-17648)
// ============================================================================

/** DA-S11 Restricted retention floor for client-supplied financial evidence. */
export const SOURCE_DOCUMENT_RETENTION_YEARS = 7;
export const SOURCE_DOCUMENT_CLASSIFICATION = "restricted" as const;
export const SOURCE_DOCUMENT_RETENTION_RULE_REF = "DA-S11" as const;

export const insertFinancialSourceDocumentSchema = createInsertSchema(financialSourceDocuments, {
  financialPeriodId: z.string().uuid(),
  fileName: z.string().min(1).max(500),
  fileSize: z.number().int().positive(),
  contentType: z.string().min(1).max(100).optional(),
  contentHash: z.string().regex(/^[0-9a-f]{64}$/, "contentHash must be a lowercase SHA-256 hex digest"),
  storagePath: z.string().min(1).max(1000),
  storageProvider: z.enum(["filesystem", "r2"]),
  classification: z.literal(SOURCE_DOCUMENT_CLASSIFICATION).optional(),
  sc06MemoryAllowed: z.literal(false).optional(),
  retentionRuleRef: z.literal(SOURCE_DOCUMENT_RETENTION_RULE_REF).optional(),
  uploadedByUserId: z.string().min(1).max(255).nullish(),
}).omit({ id: true, createdAt: true, updatedAt: true });

export const selectFinancialSourceDocumentSchema = createSelectSchema(financialSourceDocuments);

export type FinancialSourceDocument = typeof financialSourceDocuments.$inferSelect;
export type InsertFinancialSourceDocument = typeof financialSourceDocuments.$inferInsert;
